import React from 'react';
import { titleize, humanize, unescapeHTML } from 'underscore.string';
import moment from 'moment';
import Badge from 'components/v2/Badge';
import { t } from 'i18next';
import formatMoney from 'utils/formatters/Money';
import { sluggify } from 'utils/localization/utils';
import { CONFIGURED_FEATURES, featureEnabled } from 'helpers/feature-flags';
const WEIGHT_AFFECTED_ACCESSORIALS = ['overweight'];
const ORDER_CREATION_DATE = 'ORDER_CREATION_DATE';
const PICKUP_STOP = 'PICKUP_STOP';
const DELIVERY_EMPTY = 'DELIVERY_EMPTY';
const getElapsedTime = (time) => {
    const now = moment();
    const timeToDiff = moment(time);
    const days = now.diff(timeToDiff, 'days');
    const hours = now.diff(timeToDiff, 'hours');
    const minutes = now.diff(timeToDiff, 'minutes');
    const seconds = now.diff(timeToDiff, 'seconds');
    if (days > 0) {
        return `${days}d`;
    }
    if (hours > 0) {
        return `${hours}h`;
    }
    if (minutes > 0) {
        return `${minutes}m`;
    }
    return `${seconds}s`;
};
function getStop(stops, type, empty = false) {
    if (type !== 'origin' && type !== 'destination') {
        return null;
    }
    const action = type === 'origin' ? 'pickup' : 'deliver';
    const matchingStop = stops.find((stop) => stop.action === action && stop.empty === empty);
    return matchingStop || stops[0];
}
const updatedFormatter = (_cell, row) => {
    return (React.createElement("p", { style: { color: '#00a9db' } }, paneLinkFormatter(getElapsedTime(row.modified_at || row.created_at), row)));
};
const paneLinkFormatter = (cell, row) => {
    var _a;
    const accessorialType = row === null || row === void 0 ? void 0 : row.accessorial_type;
    const weight = ((_a = row === null || row === void 0 ? void 0 : row.shipment) === null || _a === void 0 ? void 0 : _a.weight) || 0;
    return (React.createElement("div", { className: "flex-col" },
        React.createElement("a", { onClick: () => row.showAccessorialRequestPane(row) }, cell),
        featureEnabled(CONFIGURED_FEATURES.SSR) &&
            WEIGHT_AFFECTED_ACCESSORIALS.includes(accessorialType) && (React.createElement("p", { className: "py-2" },
            t('TOTAL_WEIGHT'),
            " ",
            weight,
            DEFAULT_WEIGHT_UNITS))));
};
function getAccessorialColor(status) {
    switch (status) {
        case 'pending':
            return 'yellow';
        case 'approved':
            return 'brand';
        case 'cancelled':
            return 'gray';
        default:
            return 'purple';
    }
}
const statusFormatter = (_cell, row) => {
    const { shipper_status, carrier_status } = row;
    const shipperStatus = shipper_status.startsWith('pending')
        ? 'pending'
        : shipper_status;
    return (React.createElement("div", null,
        React.createElement(Badge, { color: getAccessorialColor(shipperStatus), size: "large", rounded: true }, t('SHIPPER_STATUS_DATA', {
            shipperStatus: humanize(titleize(shipperStatus)),
        })),
        React.createElement("div", { className: "mt-4" },
            React.createElement(Badge, { color: getAccessorialColor(carrier_status), size: "large", rounded: true }, t('CARRIER_STATUS_DATA', {
                carrierStatus: humanize(titleize(carrier_status)),
            })))));
};
const deliveryFormatter = (_cell, row) => {
    if (row.shipment.deliver_after && row.shipment.deliver_by) {
        return (React.createElement("ul", null,
            React.createElement("li", null, moment(row.shipment.deliver_after).format('MMMM Do YYYY, h:mm:ss a')),
            React.createElement("li", null, moment(row.shipment.deliver_by).format('MMMM Do YYYY, h:mm:ss a'))));
    }
};
const requestedFormatter = (_cell, row) => {
    if (row.created_at) {
        return (React.createElement("ul", null,
            React.createElement("li", null, moment(row.created_at).format('MMMM Do YYYY, h:mm:ss a')),
            React.createElement("p", null,
                "(",
                t('TIMEZONE_NAME'),
                ")")));
    }
};
const approvalDueDateFormatter = (_cell, row) => {
    const { approval_due_date } = row;
    if (approval_due_date) {
        return (React.createElement("ul", null,
            React.createElement("li", null, moment(approval_due_date).format('MMMM Do YYYY, h:mm:ss a')),
            React.createElement("p", null,
                "(",
                t('TIMEZONE_NAME'),
                ")")));
    }
};
const rateSourceFormatter = (_cell) => {
    return React.createElement("div", null, t(sluggify(_cell)) || '');
};
const originFormatter = (_, row) => {
    var _a, _b;
    return React.createElement("div", null, ((_b = (_a = row.shipment) === null || _a === void 0 ? void 0 : _a.origin[0]) === null || _b === void 0 ? void 0 : _b.address) || '');
};
const destinationFormatter = (_, row) => {
    var _a, _b;
    return React.createElement("div", null, ((_b = (_a = row.shipment) === null || _a === void 0 ? void 0 : _a.destination[0]) === null || _b === void 0 ? void 0 : _b.address) || '');
};
const carrierCostFormatter = (_cell, row) => {
    return React.createElement("div", null, formatMoney(row.carrier_cost || 0, row === null || row === void 0 ? void 0 : row.currency));
};
const shipperCostFormatter = (_cell, row) => {
    return React.createElement("div", null, formatMoney(row.shipper_cost || 0, row === null || row === void 0 ? void 0 : row.currency));
};
const shipmentFormatter = (cell, row) => {
    return React.createElement("a", { onClick: () => row.showShipmentModal(row.shipment._id) }, cell);
};
const payeeTypeFormatter = (_cell, row) => {
    if (row.payee_type === 'carrier') {
        return React.createElement("div", null, row.carrier_name || '');
    }
    if (row.payee_type === 'vendor') {
        return React.createElement("div", null, row.vendor_name || '');
    }
    if (row.payee_type === 'partner') {
        return React.createElement("div", null, row.partner_name || '');
    }
};
const nameFormatter = (cell) => React.createElement("span", null, unescapeHTML(cell));
const columns = [
    {
        dataField: 'shipment_id',
        text: 'Shipment',
        sort: true,
        formatter: shipmentFormatter,
        localizationSlug: 'SHIPMENT',
        count: 1,
        titleize: true,
    },
    {
        dataField: 'equipment_id',
        text: 'Equipment',
        sort: true,
        formatter: nameFormatter,
        localizationSlug: 'EQUIPMENT',
        titleize: true,
    },
    {
        dataField: 'shipper_name',
        text: 'Shipper',
        formatter: nameFormatter,
        sort: true,
        localizationSlug: 'SHIPPER',
        count: 1,
        titleize: true,
    },
    {
        dataField: 'carrier_name',
        text: 'Carrier',
        formatter: nameFormatter,
        sort: true,
        localizationSlug: 'CARRIER',
        count: 1,
        titleize: true,
    },
    {
        dataField: 'message',
        text: 'Accessorial',
        sort: true,
        formatter: paneLinkFormatter,
        localizationSlug: 'ACCESSORIAL',
        count: 1,
        titleize: true,
    },
    {
        dataField: 'shipper_cost',
        text: 'Shipper Cost',
        formatter: shipperCostFormatter,
        sort: true,
        localizationSlug: 'SHIPPER_COST',
        titleize: true,
    },
    {
        dataField: 'receivable_rate_source',
        text: 'Shipper Source',
        formatter: rateSourceFormatter,
        sort: false,
        localizationSlug: 'SHIPPER_SOURCE',
        titleize: true,
    },
    {
        dataField: 'carrier_cost',
        text: 'Carrier Cost',
        formatter: carrierCostFormatter,
        sort: true,
        localizationSlug: 'CARRIER_COST',
        titleize: true,
    },
    {
        dataField: 'payable_rate_source',
        text: 'Carrier Source',
        formatter: rateSourceFormatter,
        sort: false,
        localizationSlug: 'CARRIER_SOURCE',
        titleize: true,
    },
    {
        dataField: 'status',
        text: 'Status',
        sort: true,
        formatter: statusFormatter,
        localizationSlug: 'STATUS',
        titleize: true,
    },
    {
        dataField: 'created_by',
        text: 'Created by',
        sort: true,
        localizationSlug: 'CREATED_BY',
        titleize: true,
    },
    {
        dataField: 'carrier_decline_reason_code',
        text: 'Reason Code',
        sort: false,
        localizationSlug: 'REASON_CODE',
        titleize: true,
    },
    {
        dataField: 'payee_type',
        text: 'Payee',
        sort: true,
        formatter: payeeTypeFormatter,
        localizationSlug: 'PAYEE',
        titleize: true,
    },
    {
        dataField: 'origin',
        text: 'Origin Address',
        sort: true,
        formatter: originFormatter,
        localizationSlug: 'ORIGIN_ADDRESS',
        titleize: true,
    },
    {
        dataField: 'destination',
        text: 'Destination Address',
        sort: true,
        formatter: destinationFormatter,
        localizationSlug: 'DESTINATION_ADDRESS',
        titleize: true,
    },
    {
        dataField: 'action_required',
        text: 'Action Required',
        sort: true,
        formatter: paneLinkFormatter,
        localizationSlug: 'ACTION_REQUIRED',
        titleize: true,
    },
    {
        dataField: 'approval_due_date',
        text: 'Approval Due Date',
        sort: true,
        formatter: approvalDueDateFormatter,
        localizationSlug: 'APPROVAL_DUE_DATE',
        titleize: true,
    },
    {
        dataField: 'delivery_by',
        text: 'Delivery',
        sort: false,
        formatter: deliveryFormatter,
        localizationSlug: 'DELIVERY',
        titleize: true,
    },
    {
        dataField: 'created_at',
        text: 'Requested',
        sort: false,
        formatter: requestedFormatter,
        localizationSlug: 'REQUESTED',
        titleize: true,
    },
    {
        dataField: 'modified_at',
        text: 'Updated',
        sort: true,
        formatter: updatedFormatter,
        localizationSlug: 'UPDATED',
        titleize: true,
    },
];
export default {
    columns,
};
